// Constants
import { Environment } from "@ms/uno-constants/lib/local/AppConstants";
import { WebQueryParams } from "@ms/uno-constants/lib/local/WebConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Generates an entry key for the PlanContext that is used in Teams.
 * The key format is <teamId.length> + '#' + <teamId> + <channelId>
 * @param teamId The team ID
 * @param channelId The channel ID
 */ export function generateBasicPlanContextKey(teamId, channelId) {
    const LENGTH_SEPARATOR = "#";
    if (teamId.length === 0) {
        throw new Error("teamId is empty");
    }
    if (channelId.length === 0) {
        throw new Error("channelId is empty");
    }
    return encodeGraphReferenceKey(`${teamId.length}${LENGTH_SEPARATOR}${teamId}${channelId}`);
}
/**
 * Encodes a given key string by replacing certain special characters with their respective percent-encoded values.
 * @param {string} key - The string to be encoded.
 * @returns {string} - The encoded string with all special characters replaced by their percent-encoded values.
 * The function replaces the following characters:
 * - '%' with '%25'
 * - '.' with '%2E'
 * - ':' with '%3A'
 * - '@' with '%40'
 * - '#' with '%23'
 */ export function encodeGraphReferenceKey(key) {
    return key.replace(/%/g, "%25").replace(/\./g, "%2E").replace(/:/g, "%3A").replace(/\@/g, "%40").replace(/\#/g, "%23");
}
/** * Extracts the teamId and channelId from the containerId
 * @param containerId The teams channel container id
 * format of containerId is "<teamId>/channels/<channelId>"
 * @returns An array containing the teamId and channelId
 */ export function extractTeamIdAndChannelIdFromContainerId(containerId) {
    const parts = containerId.split("/");
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2] || parts[1] !== "channels") {
        throw new Error(`Invalid teams channel containerId format: ${containerId}`);
    }
    const result = {
        teamId: parts[0],
        channelId: parts[2]
    };
    return result;
}
/**
 * Computes the Teams root URL based on the environment
 * @param environment The environment to determine the Teams base URL
 * @param loggerContext Optional logger context for logging unsupported environments
 * @returns The Teams root URL or null for unsupported environments
 */ export function computeTeamsRootUrl(environment, loggerContext) {
    switch(environment){
        case Environment.Ppe:
        case Environment.Prod:
        case Environment.Gcc:
            return "https://teams.microsoft.com";
        case Environment.Gcch:
            return "https://gov.teams.microsoft.us";
        case Environment.Dod:
            return "https://dod.teams.microsoft.us";
        case Environment.Bleu:
            return "https://teams.sovcloud.fr";
        case Environment.Delos:
            return "https://teams.sovcloud.de";
        case Environment.Agc:
            // TODO [ADO#8848162] - Need to confirm the URLs to use for these environments
            loggerContext?.traceLogger?.logTrace(0x1e1444c4 /* tag_4fete */ , TraceLevel.Error, `computeTeamsRootUrl Unsupported environment. [Environment = ${environment}]`);
            return null;
        default:
            loggerContext?.traceLogger?.logTrace(0x1e1444c3 /* tag_4fetd */ , TraceLevel.Error, `computeTeamsRootUrl Unknown environment. [Environment = ${environment}]`);
            return "https://teams.microsoft.com";
    }
}
/**
 * Sanitizes chat ID by taking the first ID if multiple IDs are present
 * @param chatId The chat ID to sanitize
 * @returns The sanitized chat ID
 */ export function sanitizeChatId(chatId) {
    // If the chatId contains multiple Ids separated by semicolons, take the first one
    // Example: Input: chatId_123;chatId_456;chatId_789 -> Output: chatId_123
    return chatId?.trim().split(";")[0];
}
/**
 * Ensures a URL contains the correct tenant ID ('tid') query parameter.
 * If the URL already contains a tid parameter, it is replaced with the provided value.
 * Returns the URL unchanged if tenantId is empty or the URL is malformed.
 * @param websiteUrl The URL to normalize
 * @param tenantId The tenant ID to set
 */ export function normalizeTenantIdInWebsiteUrl(websiteUrl, tenantId) {
    if (!tenantId || !websiteUrl) {
        return websiteUrl;
    }
    try {
        const url = new URL(websiteUrl);
        url.searchParams.set(WebQueryParams.TenantId, tenantId);
        return url.toString();
    } catch  {
        // If the URL is malformed, return it unchanged
        return websiteUrl;
    }
}
